import { ActionIcon, Group, Stack, Text, Title } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import type { ReactNode } from "react";

interface PageProps {
  /** Page heading. */
  title?: ReactNode;
  /** Muted sub-heading shown under the title. */
  description?: ReactNode;
  /** Right-aligned controls in the header (e.g. a ViewSwitch). */
  actions?: ReactNode;
  /** When set, renders a back button before the title. */
  onBack?: () => void;
  /** Constrain content width (centered). Useful for forms. */
  maw?: number | string;
  children: ReactNode;
}

/**
 * Shared page shell. Every page renders through this so headers, spacing and
 * width all behave identically across the app.
 */
export default function Page({ title, description, actions, onBack, maw, children }: PageProps) {
  const hasHeader = title || description || actions || onBack;

  return (
    <Stack gap="xl" maw={maw} mx={maw ? "auto" : undefined} w="100%">
      {hasHeader && (
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
          <Group gap="sm" align="center" wrap="nowrap">
            {onBack && (
              <ActionIcon variant="subtle" size="lg" onClick={onBack} aria-label="Back">
                <IconArrowLeft size={18} />
              </ActionIcon>
            )}
            {(title || description) && (
              <Stack gap={2}>
                {title && <Title order={1}>{title}</Title>}
                {description && (
                  <Text c="dimmed" size="lg">
                    {description}
                  </Text>
                )}
              </Stack>
            )}
          </Group>
          {actions}
        </Group>
      )}
      {children}
    </Stack>
  );
}
